import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { RemotionGraphic } from '../components/graphics/RemotionGraphics';

const {
  FiChevronRight,
  FiHome,
  FiMapPin,
  FiCheckCircle,
  FiNavigation,
  FiUsers,
  FiArrowRight
} = FiIcons;

const coverageAreas = [
  {
    area: 'North Tyneside',
    slug: 'north-tyneside',
    summary:
      'Strong local care coordination across the coast-to-estuary corridor, with support for care plans, assessments and regular home visits.',
    keyLocations: ['North Shields', 'Tynemouth', 'Whitley Bay', 'Wallsend'],
    villages: ['Cullercoats', 'Longbenton', 'Monkseaton', 'Shiremoor'],
    note: 'Family-friendly care continuity for routine visits and short-notice support.',
    mapVariant: 'area-map-north-tyneside',
    queryHint: 'North Tyneside home care'
  },
  {
    area: 'Newcastle',
    slug: 'newcastle',
    summary:
    'Urban and suburban care coverage across Newcastle and nearby estates, including personal support, companionship and routine support coordination.',
    keyLocations: ['Newcastle City Centre', 'Jesmond', 'Heaton', 'Gosforth'],
    villages: ['Byker', 'Fawdon', 'West Denton', 'South Jesmond'],
    note: 'Fast-response scheduling is available throughout the city and commuter belt.',
    mapVariant: 'area-map-newcastle',
    queryHint: 'Newcastle home care services'
  },
  {
    area: 'Gateshead',
    slug: 'gateshead',
    summary:
      'Care support across Gateshead communities for daily support, mobility support, and family-oriented planning.',
    keyLocations: ['Gateshead Town Centre', 'Bensham', 'Whickham', 'Felling'],
    villages: ['Rowlands Gill', 'Low Fell', 'Birtley', 'Dunston'],
    note: 'Coverage is available for both short-term and long-term care arrangements.',
    mapVariant: 'area-map-gateshead',
    queryHint: 'Gateshead care support'
  },
  {
    area: 'Hexham',
    slug: 'hexham',
    summary:
      'Northumberland-side service coverage around Hexham, supporting both village-side and town-side clients.',
    keyLocations: ['Hexham', 'Bellingham', 'Haydon Bridge', 'Prudhoe Corridor'],
    villages: ['Allendale', 'Wark', 'Acomb', 'Chollerford'],
    note: 'Care plans are adapted to rural travel and local health-service access needs.',
    mapVariant: 'area-map-hexham',
    queryHint: 'Hexham home care'
  },
  {
    area: 'Corbridge',
    slug: 'corbridge',
    summary:
      'Reliable support around Corbridge with consistent care delivery for both independent and higher-dependence support cases.',
    keyLocations: ['Corbridge', 'Bywell', 'Newbiggin', 'Fourstones'],
    villages: ['Henshaw', 'Chollerford', 'Sandhoe', 'Aydon'],
    note: 'We support continuity of care with structured care handover and routine review.',
    mapVariant: 'area-map-corbridge',
    queryHint: 'Corbridge care near me'
  },
  {
    area: 'Riding Mill',
    slug: 'riding-mill',
    summary:
      'Rural and semi-rural home care support for Riding Mill and the surrounding route network.',
    keyLocations: ['Riding Mill', 'Newbrough', 'Bywell', 'Plenmeller'],
    villages: ['Broomhaugh', 'Slaley', 'Healey', 'Stocksfield'],
    note: 'Flexible scheduling is designed for clients in quieter residential and village settings.',
    mapVariant: 'area-map-riding-mill',
    queryHint: 'Riding Mill community care'
  },
  {
    area: 'Stocksfield',
    slug: 'stocksfield',
    summary:
      'Coverage around Stocksfield and adjoining settlements with coordinated handover and continuity planning.',
    keyLocations: ['Stocksfield', 'Broomley', 'New Ridley', 'Bywell'],
    villages: ['Mickley', 'Ovingham', 'Broomhaugh', 'Hindley'],
    note: 'We support both planned and urgent care requests for local families.',
    mapVariant: 'area-map-stocksfield',
    queryHint: 'Stocksfield home care support'
  },
  {
    area: 'Prudhoe',
    slug: 'prudhoe',
    summary:
      'Consistent home care support around Prudhoe with options for companionship, personal care and coordination around nearby clinics.',
    keyLocations: ['Prudhoe', 'Wylam', 'Ovingham', 'Newburn'],
    villages: ['Crawcrook', 'Mickley', 'Ryton', 'Heddon-on-the-Wall'],
    note: 'Support is arranged around your routine with family communication as a priority.',
    mapVariant: 'area-map-prudhoe',
    queryHint: 'Prudhoe local care support'
  }
];

const serviceMap = {
  fallback: 'https://www.openstreetmap.org/#map=9/55.01/-1.91'
};

const schema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'KCG',
  url: 'https://kcg.care',
  areaServed: coverageAreas.map((area) => ({
    '@type': 'Place',
    name: area.area
  }))
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Do you cover surrounding villages too?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We operate in all listed core areas and can check availability in nearby villages and postcodes on request.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is support available in both urban and rural areas?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We work across both city zones and rural routes across the North East service clusters.'
      }
    }
  ]
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://kcg.care/'
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Where We Operate',
      item: 'https://kcg.care/where-we-operate'
    }
  ]
};

const ServiceAreas = () => {
  return (
    <>
      <Helmet>
        <title>Where We Operate | KCG | North Tyneside, Newcastle, Northumberland</title>
        <meta
          name="description"
          content="KCG operates in Newcastle, North Tyneside, Gateshead, Hexham, Corbridge, Riding Mill, Stocksfield, and Prudhoe with surrounding-village coverage."
        />
        <meta
          name="keywords"
          content="KCG service areas, home care North Tyneside, Newcastle care services, Gateshead carers, Hexham care, Corbridge home care, Prudhoe home care"
        />
        <meta property="og:title" content="Where We Operate | KCG | North Tyneside, Newcastle, Northumberland" />
        <meta
          property="og:description"
          content="Care support in North East areas including North Tyneside, Newcastle, Gateshead, Hexham, Corbridge, Riding Mill, Stocksfield and Prudhoe."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:url" content="https://kcg.care/where-we-operate" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <section className="relative bg-gradient-to-br from-primary-50 to-secondary-50 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(16,76,66,0.10),_transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 text-sm text-gray-600" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li>
                <Link to="/" className="hover:text-primary-900">
                  Home
                </Link>
              </li>
              <li>
                <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
              </li>
              <li className="font-semibold text-gray-900">Where We Operate</li>
            </ol>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-900 text-sm font-medium mb-5">
              <SafeIcon icon={FiNavigation} className="w-4 h-4 mr-2" />
              KCG service map
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-5">
              We Operate Across the North East
            </h1>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Covering major locations in North Tyneside, Newcastle, Gateshead, Hexham, Corbridge, Riding Mill,
              Stocksfield and Prudhoe, plus surrounding villages where service teams can travel.
            </p>
          </motion.div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
            {coverageAreas.map((item) => (
              <a
                key={`area-link-${item.slug}`}
                href={`#${item.slug}`}
                className="bg-white border border-primary-100 rounded-xl px-3 py-2 text-sm font-medium text-gray-800 hover:text-primary-900 hover:border-primary-300 transition-colors text-center"
              >
                {item.area}
              </a>
            ))}
          </div>

        </div>
      </section>

      <section className="py-20 bg-white" id="service-map">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-4 mb-10"
          >
            <div className="inline-flex items-center text-primary-900 font-semibold">
              <SafeIcon icon={FiMapPin} className="w-5 h-5 mr-2" />
              Service map
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
              <div className="xl:col-span-2">
                <div className="bg-white border border-primary-100 rounded-2xl shadow-lg overflow-hidden">
                  <RemotionGraphic
                    variant="service-map"
                    className="w-full h-[460px] sm:h-[520px]"
                    ariaLabel="Animated map showing KCG service coverage across the North East"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  <a
                    href={serviceMap.fallback}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-900 font-medium hover:text-primary-700"
                  >
                    Open a fully interactive map
                  </a>
                </p>
              </div>

              <aside className="bg-primary-50 rounded-2xl border border-primary-100 p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-5">Coverage focus</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  We discuss availability for each postcode before agreeing care.
                  Click a region below for local area details.
                </p>

                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Main service regions</h3>
                  <ul className="space-y-2 text-gray-700">
                    {coverageAreas.map((item) => (
                      <li key={`quick-${item.slug}`} className="flex items-start">
                        <span className="mt-2 w-2 h-2 rounded-full bg-primary-900 mr-3" />
                        <span>{item.area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50" id="coverage-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Major locations and surrounding villages</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Explore each service area and nearby settlements where we commonly operate.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {coverageAreas.map((item, index) => (
              <motion.article
                key={item.area}
                id={item.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl border border-primary-100 overflow-hidden shadow-sm"
              >
                <div className="aspect-[2/1] min-h-[260px] bg-primary-50 overflow-hidden">
                  <RemotionGraphic
                    variant={item.mapVariant}
                    className="w-full h-full"
                    ariaLabel={`Map showing KCG coverage around ${item.area}`}
                  />
                </div>

                <div className="p-6 lg:p-8">
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-2xl font-bold text-gray-900">{item.area}</h3>
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-primary-900 flex-shrink-0" />
                  </div>

                  <p className="mt-3 text-gray-700 leading-relaxed">{item.summary}</p>

                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(item.queryHint)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm font-medium text-primary-900 mt-4 hover:text-primary-700"
                  >
                    View area on map
                    <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
                  </a>

                  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Major locations</h4>
                      <ul className="space-y-1">
                        {item.keyLocations.map((location) => (
                          <li key={`${item.slug}-${location}`} className="text-gray-700 flex items-start">
                            <span className="mt-2 mr-2 w-1.5 h-1.5 rounded-full bg-primary-600" />
                            <span>{location}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Surrounding villages</h4>
                      <ul className="space-y-1">
                        {item.villages.map((village) => (
                          <li key={`${item.slug}-${village}`} className="text-gray-700 flex items-start">
                            <span className="mt-2 mr-2 w-1.5 h-1.5 rounded-full bg-secondary-700" />
                            <span>{village}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <p className="mt-6 text-sm text-gray-600 border-t border-primary-100 pt-4">
                    {item.note}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-secondary-50 border border-secondary-100 rounded-2xl p-6 lg:p-8">
            <div className="flex items-start gap-3 mb-6">
              <SafeIcon icon={FiHome} className="w-6 h-6 text-primary-900 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Coverage FAQ</h2>
                <p className="text-gray-600 mt-2">Common questions about area availability and nearby villages.</p>
              </div>
            </div>

            <div className="space-y-4">
              <details className="rounded-xl bg-white border border-gray-200 p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  Is coverage available in every village you listed nearby?
                </summary>
                <p className="mt-3 text-gray-700">
                  We cover core settlements and surrounding villages where staffing and route planning allow. Contact us for confirmation per postcode.
                </p>
              </details>

              <details className="rounded-xl bg-white border border-gray-200 p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  Can new families outside the main locations request care?
                </summary>
                <p className="mt-3 text-gray-700">
                  Yes. If your address is nearby, we often can arrange care. Contact us with your postcode to ask about availability.
                </p>
              </details>

            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-primary-900 text-white rounded-2xl p-8 lg:p-12"
          >
            <div className="flex items-start gap-4">
              <SafeIcon icon={FiUsers} className="w-8 h-8 text-primary-100 mt-1" />
              <div>
                <h2 className="text-3xl font-bold mb-3">Need care in one of these areas?</h2>
                <p className="text-primary-50 max-w-3xl leading-relaxed">
                  Confirm care availability for your full address, even if you are in one of the surrounding villages.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center bg-white text-primary-900 px-6 py-3 rounded-full font-semibold hover:bg-primary-50"
                  >
                    <SafeIcon icon={FiMapPin} className="mr-2 w-5 h-5" />
                    Check Area Coverage
                  </Link>
                  <Link
                    to="/request-care"
                    className="inline-flex items-center justify-center border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-primary-900 transition-colors"
                  >
                    Start a Care Enquiry
                    <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </>
  );
};

export default ServiceAreas;
